import { ReadonlyVec3 } from 'gl-matrix';
import type { PlaneView } from '~/engine/view/views/PlaneView';
import {
    CrossSectionPlane,
    crossSectionPlanesShareSlice,
} from '~/engine/util/algorithms/sdf/cross-section';
import {
    LineInProgress,
    strokeLineInProgress,
    syncCanvasSizeToLayout,
    tracePolyline,
} from '../overlayCanvas';

/** The line being drawn, with the plane of its first point. */
export interface CrossSectionLinePreview extends LineInProgress {
    plane: CrossSectionPlane;
}

/** The closed contours drawn on one plane, in patient mm. */
export interface CrossSectionPlaneContours {
    plane: CrossSectionPlane;
    polygonsMm: readonly (readonly ReadonlyVec3[])[];
}

/** A snapshot of the plane a view displays; later view changes do not move it. */
export function crossSectionPlaneOfView(view: PlaneView): CrossSectionPlane {
    const { point, normal } = view.plane;
    return { originMm: point, normal };
}

/** Everything the overlays repaint from; one state serves every view. */
export interface CrossSectionOverlayState {
    contours: readonly CrossSectionPlaneContours[];
    line: CrossSectionLinePreview | null;
}

const CONTOUR_WIDTH_PX = 2;
/** Dashes mark a completed contour as input not yet written to the structure. */
const CONTOUR_DASH_PX = [6, 4];
/** The operation-preview green, rgb (0.2, 1.0, 0.4), opaque. */
const STROKE_STYLE = 'rgba(51, 255, 102, 1)';

export class CrossSectionOverlayRenderer {
    private readonly view: PlaneView;
    private context: CanvasRenderingContext2D | null = null;

    public constructor(view: PlaneView) {
        this.view = view;
    }

    /** Full repaint: only what was drawn on the plane the view shows now is painted. */
    public render(state: CrossSectionOverlayState): void {
        const ctx = this.getContext();
        if (!ctx) {
            return;
        }
        syncCanvasSizeToLayout(ctx);
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        // Read as geometry at every render: a view keeps its label when its slice moves.
        const viewPlane = crossSectionPlaneOfView(this.view);
        const shows = (plane: CrossSectionPlane) =>
            crossSectionPlanesShareSlice(plane, viewPlane);
        for (const entry of state.contours) {
            if (shows(entry.plane)) {
                this.drawContours(ctx, entry.polygonsMm);
            }
        }
        if (state.line && shows(state.line.plane)) {
            strokeLineInProgress(ctx, this.view, state.line, STROKE_STYLE);
        }
    }

    /** Clears the overlay and drops the context, which would otherwise keep a detached canvas alive. */
    public dispose(): void {
        const ctx = this.getContext();
        if (ctx) {
            ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        }
        this.context = null;
    }

    private getContext(): CanvasRenderingContext2D | null {
        const canvas = this.view.get2DCanvas;
        if (!canvas) {
            this.context = null;
            return null;
        }
        if (this.context?.canvas !== canvas) {
            this.context = canvas.getContext('2d');
        }
        return this.context;
    }

    private drawContours(
        ctx: CanvasRenderingContext2D,
        polygons: readonly (readonly ReadonlyVec3[])[]
    ): void {
        const rings = polygons.filter((polygon) => polygon.length >= 3);
        if (rings.length === 0) {
            return;
        }
        ctx.beginPath();
        for (const ring of rings) {
            tracePolyline(ctx, this.view, ring);
            ctx.closePath();
        }
        ctx.setLineDash(CONTOUR_DASH_PX);
        ctx.strokeStyle = STROKE_STYLE;
        ctx.lineWidth = CONTOUR_WIDTH_PX;
        ctx.stroke();
        ctx.setLineDash([]);
    }
}
